import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, formatCount } from '@wit-support/ui';
import { ContentMultiple } from './content-badges.js';
import { CONTENT_NETWORK_LABEL_RU, CONTENT_NOUN_FORMS, CONTENT_TIER_LABEL_RU, } from './content-labels.js';
import { NetworkChip } from './content-network-chip.js';
import { cn } from './lib/cn.js';
import { formatMultiple } from './lib/format-multiple.js';
import { CONTENT_NETWORKS } from './tokens/content.js';
// ---------------------------------------------------------------------------
// Сила клипа по сетям — ряд цветных чипов вместо текстового блока
// ---------------------------------------------------------------------------
/** Порядок ярусов от лучшего: «лучший ярус клипа» — первый найденный. */
const TIER_RANK = { strong: 0, working: 1, weak: 2, no_data: 3 };
/**
 * Ярус клипа одной строкой таблицы: в сети `network`, если фильтр по сети
 * задан, иначе лучший ярус по всем сетям.
 */
export function contentBestTier(byNetwork, network) {
    if (network)
        return byNetwork[network].tier;
    let best = 'no_data';
    for (const n of CONTENT_NETWORKS) {
        const t = byNetwork[n].tier;
        if (TIER_RANK[t] < TIER_RANK[best])
            best = t;
    }
    return best;
}
/** «Instagram: ×2,1 · сильный · 12 роликов» — доступное имя и подсказка чипа силы. */
export function contentStrengthTitleRu(network, s) {
    return `${CONTENT_NETWORK_LABEL_RU[network]}: ${formatMultiple(s.strength)} · ${CONTENT_TIER_LABEL_RU[s.tier]} · ${formatCount(s.uses, CONTENT_NOUN_FORMS.videos)}`;
}
/**
 * Сила клипа по сетям: чип в цвете сети + кратность с тоном (≥ ×3 зелёным,
 * ≤ ×0,33 красным). Ярус и число роликов — в подсказке и доступном имени,
 * чтобы ячейка оставалась одной компактной строкой. Сети без данных не
 * показываются; если данных нет нигде — «нет данных».
 */
export function ContentStrengthChips({ byNetwork, network, className }) {
    const nets = (network ? [network] : CONTENT_NETWORKS).filter((n) => byNetwork[n].tier !== 'no_data');
    if (nets.length === 0) {
        return (_jsx("span", { className: cn('text-[var(--color-text-muted)]', className), children: CONTENT_TIER_LABEL_RU.no_data }));
    }
    return (_jsx("span", { className: cn('flex flex-wrap gap-1', className), children: nets.map((n) => {
            const s = byNetwork[n];
            const title = contentStrengthTitleRu(n, s);
            return (_jsx("span", { title: title, "aria-label": title, role: "group", "data-tier": s.tier, children: _jsx(NetworkChip, { network: n, children: _jsx(ContentMultiple, { value: s.strength }) }) }, n));
        }) }));
}
// ---------------------------------------------------------------------------
// Позиция клипа в ролике
// ---------------------------------------------------------------------------
/** 0 — «первый», 1 — «2-й»…; отрицательная — позиция неизвестна. */
export function contentClipPositionRu(position) {
    if (position < 0)
        return '—';
    return position === 0 ? 'первый' : `${position + 1}-й`;
}
// ---------------------------------------------------------------------------
// Двойники и эксперименты
// ---------------------------------------------------------------------------
export const CONTENT_TWIN_STATUS_LABEL_RU = {
    auto: 'найден автоматически',
    confirmed: 'подтверждён',
    rejected: 'отклонён',
};
export const CONTENT_TWIN_STATUS_BADGE_VARIANT = {
    auto: 'info',
    confirmed: 'success',
    rejected: 'neutral',
};
export const CONTENT_EXPERIMENT_STATUS_LABEL_RU = {
    active: 'идёт',
    done: 'завершён',
};
export function ContentExperimentStatusBadge({ status }) {
    return (_jsx(Badge, { variant: status === 'active' ? 'success' : 'neutral', "data-status": status, children: CONTENT_EXPERIMENT_STATUS_LABEL_RU[status] }));
}
/**
 * Вывод по плечу против контроля: весь 95 % интервал выше 1 — лучше, весь
 * ниже — хуже, иначе разницы не видно. Мало данных — не вывод.
 */
export function contentExperimentVerdict(d) {
    if (d.insufficient || d.ratio === null)
        return 'insufficient';
    if (d.ratioLow !== null && d.ratioLow > 1)
        return 'better';
    if (d.ratioHigh !== null && d.ratioHigh < 1)
        return 'worse';
    return 'same';
}
export const CONTENT_EXPERIMENT_VERDICT_LABEL_RU = {
    better: 'лучше контроля',
    worse: 'хуже контроля',
    same: 'разницы не видно',
    insufficient: 'мало данных',
};
const VERDICT_VARIANT = {
    better: 'success',
    worse: 'danger',
    same: 'neutral',
    insufficient: 'outline',
};
/** «×1,2 (×1,05–×1,4) · лучше контроля» — цвет по выводу. */
export function ContentExperimentDiff({ diff }) {
    const verdict = contentExperimentVerdict(diff);
    return (_jsxs("span", { className: "inline-flex flex-wrap items-center gap-1.5 tabular-nums", children: [verdict !== 'insufficient' ? (_jsxs("span", { children: [formatMultiple(diff.ratio), diff.ratioLow !== null && diff.ratioHigh !== null ? (_jsxs("span", { className: "text-[var(--color-text-muted)]", children: [' ', "(", formatMultiple(diff.ratioLow), "\u2013", formatMultiple(diff.ratioHigh), ")"] })) : null] })) : null, _jsx(Badge, { variant: VERDICT_VARIANT[verdict], "data-verdict": verdict, children: CONTENT_EXPERIMENT_VERDICT_LABEL_RU[verdict] })] }));
}
//# sourceMappingURL=content-clips-kit.js.map